import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { Observable, of, throwError } from 'rxjs';
import { catchError, finalize, map, mergeMap } from 'rxjs/operators';
import { noInternetMessage } from '../../../utils/popup-messages';
import { ApiCallsService } from './api-calls.service';

@Injectable({
  providedIn: 'root',
})
export class TokenInterceptorService implements HttpInterceptor {
  private activeRequests = 0;
  private isToastDisplayed = false;

  constructor(
    private readonly admin: ApiCallsService,
    private readonly _toast: ToastrService,
  ) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (!navigator.onLine) {
      this.admin.loader(false);
      if (!this.isToastDisplayed) {
        this._toast.error(noInternetMessage.errorMessage);
        this.isToastDisplayed = true;
        setTimeout(() => (this.isToastDisplayed = false), 5000);
      }
      return throwError(() => new Error(noInternetMessage.errorMessage));
    }

    const token = localStorage.getItem('adminToken') ?? '';
    let encryptedBody = req.body;
    // Skip encryption for the specific URL
    if (
      !req.url.endsWith('custom-oauth/url') &&
      !req.url.endsWith('custom-oauth/callback') &&
      !req.url.endsWith('custom-oauth/identity-providers')
    ) {
      encryptedBody = this.admin.encryptData(req.body);
    }
    let isGetOrHead = req.method === 'GET' || req.method === 'HEAD';
    let modifiedReq = req;

    if (!isGetOrHead) {
      modifiedReq = req.clone({
        body: encryptedBody,
        responseType: 'text' as 'json',
      });
    } else {
      modifiedReq = req.clone({
        responseType: 'text' as 'json',
      });
    }
    if (token) {
      modifiedReq = modifiedReq.clone({
        headers: modifiedReq.headers.set('authorization', 'Bearer ' + atob(token)),
      });
    }
    if (this.admin.showLoader) {
      this.startLoader();
    } else {
      this.admin.showLoader = true;
    }
    return next.handle(modifiedReq).pipe(
      mergeMap((event) => this.handleResponse(event, req)),
      catchError((err) => this.handleError(err)),
      finalize(() => this.stopLoader()),
    );
  }

  private startLoader() {
    this.activeRequests++;
    if (this.activeRequests === 1) {
      this.admin.loader(true); // actually show the loader
    }
  }

  private stopLoader() {
    this.activeRequests = Math.max(0, this.activeRequests - 1);
    if (this.activeRequests === 0) {
      this.admin.loader(false); // hide only when all requests done
    }
  }

  private handleResponse(
    event: HttpEvent<any>,
    req?: HttpRequest<any>,
  ): Observable<HttpEvent<any>> {
    if (event instanceof HttpResponse) {
      // Skip decryption for OAuth endpoints
      if (
        req &&
        (req.url.endsWith('custom-oauth/url') ||
          req.url.endsWith('custom-oauth/callback') ||
          req.url.endsWith('custom-oauth/identity-providers'))
      ) {
        return of(event); // return response as-is
      }
      // Normal flow: decrypt
      let decrypted$ = this.admin.decryptData(event.body);
      if (!(decrypted$ instanceof Observable)) {
        decrypted$ = of(decrypted$);
      }
      return decrypted$.pipe(
        map((decryptedData: any) => {
          return event.clone({ body: decryptedData });
        }),
        catchError(() => {
          return of(event.clone({ body: { error: 'Decryption failed' } }));
        }),
      );
    }
    return of(event);
  }

  private handleError(err: any): Observable<never> {
    if (err instanceof HttpErrorResponse) {
      let decrypted$ = this.admin.decryptData(err.error);

      if (
        decrypted$.statusCode === 401 ||
        decrypted$.statusCode === 520 ||
        decrypted$.status_code === 401 ||
        decrypted$.status_code === 520
      ) {
        localStorage.removeItem('adminToken');
        localStorage.removeItem('adminDetail');
        this.admin.errorAlert(decrypted$.error ?? decrypted$.message, true, '');
      } else if (
        ((decrypted$.error && decrypted$.statusCode !== 405) ||
          (decrypted$.content && decrypted$.status_code !== 405)) &&
        ((decrypted$.error && decrypted$.statusCode !== 470) ||
          (decrypted$.content && decrypted$.status_code !== 470))
      ) {
        console.error('Error from API:', decrypted$);
        this._toast.clear();
        this._toast.error(decrypted$.error ?? decrypted$.content.detail ?? decrypted$.message, '', {
          timeOut: 2000,
          easeTime: 0,
          tapToDismiss: true,
        });
      }
    }
    return throwError(() => err);
  }
}
